/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useInterval } from 'react-use';

import * as API from '../../api/session';
import { InactivityModal } from '../../components/inactivity/InactivityModal/InactivityModal';
import { SessionRevokedModal } from '../../components/inactivity/SessionRevokedModal/SessionRevokedModal';
import {
  selectIpAddressRestriction,
  selectSessionInactive,
  selectSessionRevoked,
  isAuthenticated
} from '../../store/session/selectors';

// How often to query the server to see when the session will expire due
// to inactivity (by default, one minute).
const CHECK_INACTIVITY_TIMEOUT_FREQUENCY = 60 * 1000;

// How many minutes before the session becomes inactive we should show
// a warning to the user.
const INACTIVITY_WARNING_THRESHOLD = 5;

/**
 * Starts a timer that will show an inactivity modal if the user has not done anything to make the authentication
 * session active.
 */
const InactivityManager = () => {
  const [inactivityExpiration, setInactivityExpiration] = useState(undefined);
  const [showModal, setShowModal] = useState(false);
  const isSessionInactive = useSelector(selectSessionInactive);
  const isSessionRevoked = useSelector(selectSessionRevoked);
  const ipAddressRestriction = useSelector(selectIpAddressRestriction);
  const isUserLoggedIn = useSelector(isAuthenticated);
  const { pathname } = useLocation();

  const isOnLoginPage = pathname?.endsWith('/login');

  // When IAM session support is disabled, the inactivity expiration value is
  // undefined: it is numeric only when IAM sessions are supported and thus
  // we could possibly need to warn the user of an impending session expiration.
  const isSessionSupportEnabled = Number.isInteger(inactivityExpiration);

  // The authentication session is expired if we have already been told the
  // session is inactive, or if the inactivity expiration value (i.e. how many
  // minutes until the session becomes inactive) is negative or zero.
  const isExpired =
    isSessionInactive || (isSessionSupportEnabled && inactivityExpiration <= 0);

  // If the session has not yet expired, subtract the warning threshold
  // (so we alert the user a few minutes before the actual expiration)
  // and convert to milliseconds. If it has expired, set it to null so that
  // we do not enable the interval to show the warning modal at all.
  const warningDelayInMs =
    isSessionSupportEnabled && !isExpired
      ? (inactivityExpiration - INACTIVITY_WARNING_THRESHOLD) * 60 * 1000
      : null;

  // If the session is inactive, or if the warning modal isn't currently being
  // shown and the time before expiration has elapsed, state that the modal
  // should be shown. Note that we will not show the modal on the login page.
  const shouldModalBeShown =
    !isOnLoginPage &&
    (isSessionInactive ||
      (!showModal && isSessionSupportEnabled && warningDelayInMs < 0));

  // If the warning modal is currently being shown and the session expires later
  // than the warning threshold (i.e. if the session got re-activated), then
  // the modal should be hidden.
  const shouldModalBeHidden =
    showModal &&
    (!isSessionSupportEnabled ||
      inactivityExpiration > INACTIVITY_WARNING_THRESHOLD);

  useEffect(() => {
    if (shouldModalBeShown) {
      setShowModal(true);
    } else if (shouldModalBeHidden) {
      setShowModal(false);
    }
  }, [shouldModalBeShown, shouldModalBeHidden, setShowModal]);

  // If the user is logged in, check the authentication session information
  // periodically; otherwise, do not start the timer at all.
  const checkSessionFrequency = isUserLoggedIn
    ? CHECK_INACTIVITY_TIMEOUT_FREQUENCY
    : null;

  const updateInactivityExpiration = useCallback(() => {
    const fetchData = async () => {
      // Specify that getting the session data should NOT force the refresh
      // token to be refreshed (since doing so would reset the activity in
      // the session, which we do not want to do).
      setInactivityExpiration(await API.fetchSessionData(false));
    };

    // Invoke the data request inside an async function (and ignore its
    // return value) since making the entire callback function async would
    // print a warning saying that use effects can not return a promise.
    fetchData();
  }, [setInactivityExpiration]);

  // Immediately update the inactivity expiration information.
  useEffect(updateInactivityExpiration, [updateInactivityExpiration]);

  // Also start a timer that every minute will query the server to see when the
  // current authentication session will expire due to inactivity.
  useInterval(updateInactivityExpiration, checkSessionFrequency);

  // Whenever the inactivity expiration date changes, set a timer that
  // will pop a few minutes before the session is set to expire.
  useInterval(
    () => setShowModal(true),
    warningDelayInMs > 0 ? warningDelayInMs : null
  );

  // When the inactivity modal closes, make the modal disappear and invoke
  // the API that will get the session data while forcing the server
  // to refresh the session (thus preventing the session from actually becoming
  // inactive).
  const closeInactivityModalCallback = useCallback(async () => {
    setShowModal(false);
    setInactivityExpiration(await API.fetchSessionData(true));
  }, [setShowModal, setInactivityExpiration]);

  // If we are showing the modal, convert the inactivity expiration into its
  // string equivalent; otherwise, send an empty string, to prevent the
  // inactivity modal to re-render unnecessarily when not being shown.
  const minutesRemainingString = showModal ? String(inactivityExpiration) : '';

  return (
    <>
      <InactivityModal
        isOpen={showModal}
        minutesRemaining={minutesRemainingString}
        isExpired={isExpired}
        onClose={closeInactivityModalCallback}
      />
      <SessionRevokedModal
        isOpen={isSessionRevoked || ipAddressRestriction}
        ipAddressRestriction={ipAddressRestriction}
      />
    </>
  );
};

export default InactivityManager;
